import { ECurecallNotificationAppearance } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useNotification } from 'src/providers/notification.provider'
import { PatientService } from 'src/services/patient.service'
import { useDebouncedCallback } from 'use-debounce'
import { getErrorMessage } from 'src/utils/http.util'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { TPatientForSearchBar } from './searchbar.type'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'

export const useSearch = () => {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const { notify } = useNotification()
  const navigate = useLocalizedNavigate()

  const searchbarRef = useRef<HTMLInputElement>(null)
  const resultsContainerRef = useRef<HTMLDivElement>(null)
  const [showResults, setShowResults] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [searchResults, setSearchResults] = useState<Array<TPatientForSearchBar>>([])
  const [cursorIndex, setCursorIndex] = useState<number>(0)

  const _searchDebounced = useDebouncedCallback(
    async (searchValue: string, setSearchResults: Dispatch<SetStateAction<TPatientForSearchBar[]>>) => {
      if (!searchValue.trim()) {
        return setSearchResults([])
      }

      try {
        setSearchResults(await PatientService.searchPatient(project.id, searchValue))
      } catch (error) {
        notify(
          {
            title: t('pages.auth.error.server'),
            message: getErrorMessage(error),
            appearance: ECurecallNotificationAppearance.WARNING,
          },
          13000,
        )
        return setSearchResults([])
      }
    },
    300,
  )

  const handleSearchChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    !showResults && setShowResults(true)
    const newSearchValue = event.target.value
    setSearchValue(newSearchValue)
    _searchDebounced(newSearchValue, setSearchResults)
  }

  const handleOnFocus = () => setShowResults(true)

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!searchResults.length) return
    if (event.code === 'ArrowDown')
      setCursorIndex((prev) => (prev >= searchResults.length - 1 ? 0 : prev + 1))
    if (event.code === 'ArrowUp') setCursorIndex((prev) => (prev === 0 ? searchResults.length - 1 : prev - 1))
    if (event.code === 'Enter') {
      clearField()
      const selectedResult = searchResults[cursorIndex] as TPatientForSearchBar
      navigate(`/${project.id}/medical/patients/${selectedResult.id}`)
    }
  }

  const clearField = () => {
    setSearchValue('')
    setSearchResults([])
    setShowResults(false)
    setCursorIndex(0)
  }

  useEffect(() => {
    if (!showResults) return
    function handleClickOutside(event: MouseEvent) {
      const isClickingOutside = !searchbarRef.current?.contains(event.target as Node)
      if (isClickingOutside) {
        setShowResults(false)
      }
    }
    globalThis.addEventListener('click', handleClickOutside)
    return () => globalThis.removeEventListener('click', handleClickOutside)
  }, [showResults])

  // When navigating through results with keyboard scroll to the selected element automatically
  useEffect(() => {
    if (!showResults || !resultsContainerRef.current || searchResults.length === 0) return

    const selectedElement = resultsContainerRef.current.children[cursorIndex] as HTMLElement
    if (selectedElement) {
      selectedElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      })
    }
  }, [cursorIndex, showResults, searchResults.length])

  return {
    showResults,
    searchValue,
    searchbarRef,
    resultsContainerRef,
    searchResults,
    cursorIndex,
    handleSearchChange,
    handleOnFocus,
    handleKeyDown,
    clearField,
  }
}
